import DirectionsBoatTwoToneIcon from "@mui/icons-material/DirectionsBoatTwoTone";
import LocalShippingTwoToneIcon from "@mui/icons-material/LocalShippingTwoTone";
import PersonOutlineTwoToneIcon from "@mui/icons-material/PersonOutlineTwoTone";
import type { FilterFieldDefinition } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import {
  emiratesIdNumberSchema,
  passportNumberSchema,
  type NormalGatepassFormData,
} from "@features/gatepass/schema/normalGatepassFormSchema";
import type { OffshoreGatepassFormData } from "@features/gatepass/schema/offshoreGatepassFormSchema";
import type { TruckGatepassFormData } from "@features/gatepass/schema/truckGatepassFormSchema";
import type { NormalGatepassRequestPayload } from "@services/gatepass/types";
import { toGatepassRegistrationEmirate } from "@services/referenceDataApi";
import type { PortCode } from "@services/vessel-call/types";
import { paths } from "@utils/navigation";
import type { TFunction } from "i18next";
import { useCargoGatepassFormStore } from "../store/useCargoGatepassForm.store";
import { useNormalGatepassFormStore } from "../store/useNormalGatepassForm.store";
import { useOffshoreGatepassFormStore } from "../store/useOffshoreGatepassForm.store";
import { useTruckGatepassFormStore } from "../store/useTruckGatepassForm.store";
import type { GatepassFormValues, StandardVehicleFields } from "./types";

export const GATEPASS_TYPES = {
  NORMAL: "normal",
  OFFSHORE: "offshore",
  TRUCK: "truck",
  CARGO: "cargo",
};

export const getGatepassCategoryTranslationKey = (category: string): string => {
  const normalized = String(category ?? "")
    .trim()
    .toLowerCase();
  return `gatepass.${normalized}`;
};

const MANUAL_SECURITY_CLEARANCE_ROLES = new Set([
  "licensed-company-agent",
  "vendor-master",
  "security-operator",
  "security-supervisor",
]);

export const requiresManualSecurityClearanceCheck = (userRoles: string[]) =>
  userRoles.some((role) => MANUAL_SECURITY_CLEARANCE_ROLES.has(role));

export const gatepassCardTypes = [
  {
    type: GATEPASS_TYPES.NORMAL,
    name: "gatepass.types.normal.title",
    description: "gatepass.types.normal.description",
    path: `${paths.gatepass.normalGatepass}`,
    icon: <PersonOutlineTwoToneIcon color="primary" />,
  },
  {
    type: GATEPASS_TYPES.OFFSHORE,
    name: "gatepass.types.offshore.title",
    description: "gatepass.types.offshore.description",
    path: `${paths.gatepass.offshoreGatepass}`,
    icon: <DirectionsBoatTwoToneIcon color="primary" />,
  },
  {
    type: GATEPASS_TYPES.TRUCK,
    name: "gatepass.types.truck.title",
    description: "gatepass.types.truck.description",
    path: `${paths.gatepass.truckGatepass}`,
    icon: <LocalShippingTwoToneIcon color="primary" />,
  },
];

export const gatepassStoresByType = {
  [GATEPASS_TYPES.NORMAL]: useNormalGatepassFormStore,
  [GATEPASS_TYPES.OFFSHORE]: useOffshoreGatepassFormStore,
  [GATEPASS_TYPES.TRUCK]: useTruckGatepassFormStore,
  [GATEPASS_TYPES.CARGO]: useCargoGatepassFormStore,
} as any;

export const getGatePassesFilterFields = (t: TFunction): Record<string, FilterFieldDefinition> => ({
  serialNumbers: {
    label: t("gatepass.gatepass-id"),
    type: "number",
    minLength: 1,
  },
  sequenceNumbers: {
    label: t("gatepass.gatepass-no"),
    type: "number",
    minLength: 1,
  },
  sequenceGroupNumbers: {
    label: t("gatepass.group-no"),
    type: "number",
    minLength: 1,
  },
  gatepassCategory: {
    label: t("gatepass.tableHeaders.gatepassCategory"),
    type: "text",
    minLength: 3,
  },
  sponsorCompanies: {
    label: t("gatepass.sponsor-company"),
    type: "text",
    minLength: 3,
  },
  passportNumber: {
    label: t("gatepass.passport-no-or-emirates-id"),
    type: "text",
    minLength: 3,
    maxLength: 10,
  },
  emiratesIdNumber: {
    label: t("gatepass.emitates-id"),
    type: "text",
    minLength: 3,
  },
  companyName: {
    label: t("gatepass.company-name"),
    type: "text",
    minLength: 3,
  },
  fullNameEnglish: {
    label: t("gatepass.individual-name"),
    type: "text",
    minLength: 2,
  },
  submittedAt: {
    label: t("gatepass.submitted-at"),
    type: "dateFromToWithoutTime",
  },
  hazard: {
    label: t("gatepass.hazard"),
    type: "text",
    minLength: 2,
  },
  serviceNumbers: {
    label: t("gatepass.service-no"),
    type: "number",
    minLength: 1,
  },
  jobNumbers: {
    label: t("gatepass.job-no"),
    type: "number",
    minLength: 1,
  },
  gatepassStatus: {
    label: t("gatepass.gatepass-status"),
    type: "autocomplete",
    options: [
      "AWAITING_FOTT_APPROVAL",
      "AWAITING_SPONSOR_COMPANY_APPROVAL",
      "AWAITING_SECURITY_DEPT_APPROVAL",
      "APPROVED",
      "REJECTED",
      "EXPIRED",
    ].map((code) => ({
      value: code,
      label: t(`gatepass.status-labels.${code}`),
    })),
  },
  claimedBy: {
    label: t("gatepass.tableHeaders.claimedBy"),
    type: "text",
    minLength: 2,
  },
});

const isTruckGatepassData = (data: GatepassFormValues): data is TruckGatepassFormData =>
  String(data.gatepassCategory).toUpperCase() === "TRUCK";

const hasLocations = (
  data: GatepassFormValues,
): data is NormalGatepassFormData & {
  locations: Array<{ location: string; entryPurpose?: string }>;
} => "locations" in data && Array.isArray(data.locations);

const getVehicleFields = (data: GatepassFormValues): StandardVehicleFields | null => {
  if (isTruckGatepassData(data)) {
    return data.vehicles[0] ?? null;
  }

  return {
    registrationEmirate: data.registrationEmirate,
    vehicleType: data.vehicleType,
    vehicleColour: data.vehicleColour,
    category: data.category,
    plateNumber: data.plateNumber,
  };
};

const mapVehicleFields = (vehicle: StandardVehicleFields | null) => {
  if (!vehicle?.vehicleType?.trim()) {
    return null;
  }

  return {
    registrationEmirate: toGatepassRegistrationEmirate(vehicle.registrationEmirate),
    type: vehicle.vehicleType.trim().toUpperCase() || undefined,
    colour: vehicle.vehicleColour || undefined,
    category: vehicle.category || undefined,
    plateNumber: vehicle.plateNumber || undefined,
  };
};

const omitEmptyFields = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== undefined && value !== null && String(value).trim() !== "",
    ),
  ) as Partial<T>;

const mapTruckJobDetails = (truckData: TruckGatepassFormData) =>
  omitEmptyFields({
    jobNumber: truckData.jobNumber?.toString().trim() || undefined,
    jobDescription: truckData.jobDescription?.trim() || undefined,
    deliverTo: truckData.deliverTo?.trim() || undefined,
    jobStartDate: toLocalDateString(truckData.jobStartDate) || undefined,
    jobEndDate: toLocalDateString(truckData.jobEndDate) || undefined,
    serviceNumber: truckData.serviceNumber?.toString().trim() || undefined,
    destinationVcn: truckData.destinationVcn?.trim() || undefined,
    vesselName: truckData.vesselName?.trim() || undefined,
    cargoHandlingParty: truckData.cargoHandlingParty?.trim() || undefined,
  });

const mapTruckCargos = (truckData: TruckGatepassFormData) =>
  (truckData.cargos ?? [])
    .filter((cargo) => Boolean(cargo.cargo?.trim()))
    .map((cargo) =>
      omitEmptyFields({
        cargoCode: cargo.cargo?.trim() || undefined,
        unitOfMeasurement: cargo.unitOfMeasurement?.trim() || undefined,
        quantity:
          cargo.quantity === "" || cargo.quantity === null || cargo.quantity === undefined
            ? undefined
            : Number(cargo.quantity),
      }),
    );

const mapTruckDeliveryNote = (item: TruckGatepassFormData["deliveryNotesDocuments"][number]) => {
  const normalizedUrl = String(item.url ?? item.documentUrl ?? item.blobUrl ?? "").trim();

  return {
    name: String(item.name ?? "").trim() || undefined,
    url: normalizedUrl || undefined,
    type: String(item.certificateType ?? item.type ?? "").trim() || "DELIVERY_NOTES",
  };
};

export const extractScErrorDetail = (error: unknown): string =>
  String(
    (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail ?? "",
  ).trim();

export type ScSearchField = "passport" | "emirates" | null;

export const resolveScSearchField = (
  passportNumber?: string,
  emiratesIdNumber?: string,
): ScSearchField => {
  const hasPassport = Boolean(passportNumber?.trim());
  const hasEmirates = Boolean(emiratesIdNumber?.trim());

  if (hasPassport) {
    return "passport";
  }

  if (hasEmirates) {
    return "emirates";
  }

  return null;
};

export const createScIdValidators = (
  translationKey: string,
  translate: (key: string) => string,
) => {
  const passportValidationSchema = passportNumberSchema(
    15,
    translate(`${translationKey}.passport-number`),
  );
  const emiratesValidationSchema = emiratesIdNumberSchema(
    translate(`${translationKey}.emirates-id-number`),
  );

  return {
    isPassportValid: (value: string) => {
      const normalized = value.trim();
      return normalized.length > 0 && passportValidationSchema.safeParse(normalized).success;
    },
    isEmiratesIdValid: (value: string) => {
      const normalized = value.trim();
      return normalized.length > 0 && emiratesValidationSchema.safeParse(normalized).success;
    },
  };
};

const toLocalDateString = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString().slice(0, 10);
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const dayMonthYearMatch = new RegExp(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/).exec(trimmedValue);
  if (dayMonthYearMatch) {
    const [, day, month, year] = dayMonthYearMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return trimmedValue;
  }

  const isoDateMatch = new RegExp(/^(\d{4}-\d{2}-\d{2})T/).exec(trimmedValue);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }

  const parsedDate = new Date(trimmedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().slice(0, 10);
  }

  return trimmedValue;
};

function isSameCalendarDay(date1: Date, date2: Date) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function calculateTruckDuration(
  jobStartDate: string | Date | undefined,
  jobEndDate: string | Date | undefined,
) {
  if (!jobStartDate || !jobEndDate) return "DAILY";
  const startDate = jobStartDate instanceof Date ? jobStartDate : new Date(jobStartDate);
  const endDate = jobEndDate instanceof Date ? jobEndDate : new Date(jobEndDate);

  return isSameCalendarDay(startDate, endDate) ? "DAILY" : "WEEKLY";
}

// this function cand be extended for each gatepass type based on data.gatepassCategory
export function mapGatepassFormToPayload(
  data: GatepassFormValues,
  securityClearanceId: string,
  options?: { isIndividualUser?: boolean },
): NormalGatepassRequestPayload {
  const firstVehicle = getVehicleFields(data);
  const isIndividual = options?.isIndividualUser === true;

  const basePayload: NormalGatepassRequestPayload = {
    gatepassCategory: data.gatepassCategory,
    gatepassType: isIndividual ? "SINGLE" : data.gatepassType.toUpperCase(),
    vip: isIndividual ? undefined : (data.vip ?? undefined),
    requestFor: isIndividual ? undefined : data.requestFor.toUpperCase(),

    expectedEntryDate: toLocalDateString(data.expectedEntryDate) || "",
    entryType: data.entryType.toUpperCase(),
    fottEntry: data.FOTTEntry === true,

    // Vehicle object containing all vehicle details
    vehicle: mapVehicleFields(firstVehicle),

    // Locations with orderNumber
    locations: hasLocations(data)
      ? data.locations.map((loc, index) => ({
          orderNumber: index,
          locationName: loc.location,
          entryPurpose: loc.entryPurpose?.trim() || "",
        }))
      : undefined,

    groupMembers:
      data.gatepassType === "single"
        ? null
        : data.groupMembers.map((member) => ({
            passportNumber: member.passportNumber || undefined,
            emiratesIdNumber: member.emiratesIdNumber || undefined,
            fullNameEnglish: member.fullName || undefined,
            passportExpiryDate: toLocalDateString(member.passportExpiryDate) || undefined,
            mobileNumber: member.phoneNumber || undefined,
          })),

    securityClearanceId,
    visitor: {
      fullNameEnglish: data.fullName,
      passportNumber: data.passportNumber || undefined,
      emiratesIdNumber: data.emiratesIdNumber || undefined,
      passportExpiryDate: toLocalDateString(data.passportNumberExpirationDate) || undefined,
      drivingLicenseNumber:
        "driverLicenseNumber" in data ? data.driverLicenseNumber?.trim() || undefined : undefined,
      mobileNumber: data.phoneNumber || undefined,
    },
    sponsorCompany: data.sponsorCompany,
    visitorCompany: "visitorCompany" in data ? data.visitorCompany : undefined,
    visitorCompanyNameArabic: data.visitorCompanyArabic?.trim() || undefined,
    reference: data.reference?.trim() || undefined,
    port: data.port,
    gate: data.gate,
    duration: data.gatepassDuration.toUpperCase(),
    permanentMonths:
      data.gatepassDuration === "permanent" ? Number(data.permanentDuration ?? "1") : undefined,
  };

  if (String(data.gatepassCategory).toUpperCase() === "OFFSHORE") {
    const offshoreData = data as OffshoreGatepassFormData;
    const offshoreHasVehicle = Boolean(offshoreData.vehicleType?.trim());
    // Do not spread basePayload: offshore "/gatepass-requests:start" expects a slimmer contract
    return {
      gatepassCategory: String(offshoreData.gatepassCategory).toUpperCase(),
      gatepassType: offshoreData.gatepassType.toUpperCase(),
      expectedEntryDate: toLocalDateString(offshoreData.expectedEntryDate) || "",
      entryType: offshoreData.entryType.toUpperCase(),
      requestFor: offshoreData.requestFor.toUpperCase(),
      vehicle: offshoreHasVehicle
        ? {
            registrationEmirate: toGatepassRegistrationEmirate(offshoreData.registrationEmirate),
            type: offshoreData.vehicleType?.trim().toUpperCase() || undefined,
            colour: offshoreData.vehicleColour || undefined,
            category: offshoreData.category || undefined,
            plateNumber: offshoreData.plateNumber || undefined,
          }
        : null,
      securityClearanceId,
      visitor: {
        fullNameEnglish: offshoreData.fullName,
        passportNumber: offshoreData.passportNumber || undefined,
        emiratesIdNumber: offshoreData.emiratesIdNumber || undefined,
        passportExpirationDate:
          toLocalDateString(offshoreData.passportNumberExpirationDate) || undefined,
        mobileNumber: offshoreData.phoneNumber || undefined,
        entryPurpose: offshoreData.entryPurpose || undefined,
      },
      sponsorCompany: offshoreData.sponsorCompany,
      visitorCompany: offshoreData.visitorCompanyEnglish,
      visitorCompanyNameArabic: offshoreData.visitorCompanyArabic?.trim() || undefined,
      reference: offshoreData.reference?.trim() || undefined,
      port: offshoreData.port,
      gate: offshoreData.gate,
      duration: offshoreData.gatepassDuration.toUpperCase(),
      hasCamera: offshoreData.hasCamera,
      cameraDetails: offshoreData.hasCamera
        ? {
            cameraSerialNumber: offshoreData.cameraSerialNumber || undefined,
            cameraModel: offshoreData.cameraModel || undefined,
          }
        : undefined,
      vesselDetails: (offshoreData.vessels ?? []).map((vessel) => ({
        imoNumber: vessel.vesselImoNumber?.trim() || undefined,
        name: vessel.vesselName?.trim() || undefined,
      })),
    };
  }

  if (String(data.gatepassCategory).toUpperCase() === "TRUCK") {
    const truckData = data as TruckGatepassFormData;
    const cargoDestination = truckData.cargoDestination?.trim().toUpperCase() || undefined;
    const normalizedTruckType = truckData.truckType?.trim().toUpperCase() || "TRUCK";
    const jobDetails = mapTruckJobDetails(truckData);
    const mappedVehicles = (truckData.vehicles ?? [])
      .filter((vehicle) => Boolean(vehicle.vehicleType?.trim()))
      .map((vehicle) => ({
        vehicleDetails: {
          registrationEmirate: toGatepassRegistrationEmirate(vehicle.registrationEmirate),
          type: vehicle.vehicleType?.trim().toUpperCase() || undefined,
          colour: vehicle.vehicleColour || undefined,
          category: vehicle.category || undefined,
          plateNumber: vehicle.plateNumber || undefined,
        },
        drivers: (vehicle.drivers ?? []).map((driver) => ({
          fullNameEnglish: driver.fullName?.trim() || undefined,
          passportNumber: driver.passportNumber?.trim() || undefined,
          emiratesIdNumber: driver.emiratesIdNumber?.trim() || undefined,
          driverLicenseNumber: driver.driverLicenseNumber?.trim() || undefined,
          mobileNumber: driver.mobileNumber?.trim() || undefined,
          presentNationalityEnglish: truckData.nationality?.trim() || undefined,
        })),
      }));

    // Do not spread basePayload: truck "/gatepass-requests:start" expects a dedicated contract.
    return {
      gatepassCategory: String(truckData.gatepassCategory).toUpperCase(),
      gatepassType: isIndividual ? "SINGLE" : truckData.gatepassType.toUpperCase(),
      requestFor:
        isIndividual && normalizedTruckType === "TRUCK"
          ? "ENTRY"
          : isIndividual
            ? undefined
            : truckData.requestFor.toUpperCase(),
      expectedEntryDate: toLocalDateString(truckData.expectedEntryDate) || "",
      entryType: isIndividual ? "SINGLE" : truckData.entryType.toUpperCase(),
      fottEntry: truckData.FOTTEntry === true,
      groupMembers:
        truckData.gatepassType === "single"
          ? null
          : truckData.groupMembers.map((member) => ({
              passportNumber: member.passportNumber || undefined,
              emiratesIdNumber: member.emiratesIdNumber || undefined,
              fullNameEnglish: member.fullName || undefined,
              passportExpiryDate: toLocalDateString(member.passportExpiryDate) || undefined,
              mobileNumber: member.phoneNumber || undefined,
            })),
      securityClearanceId,
      visitor: {
        fullNameEnglish: truckData.fullName,
        passportNumber: truckData.passportNumber || undefined,
        emiratesIdNumber: truckData.emiratesIdNumber || undefined,
        passportExpiryDate: toLocalDateString(truckData.passportNumberExpirationDate) || undefined,
        drivingLicenseNumber: truckData.driverLicenseNumber?.trim() || undefined,
        mobileNumber: truckData.phoneNumber || undefined,
      },
      sponsorCompany: truckData.sponsorCompany,
      visitorCompany: truckData.visitorCompanyEnglish,
      visitorCompanyNameArabic: truckData.visitorCompanyArabic?.trim() || undefined,
      reference: truckData.reference,
      port: truckData.port,
      gate: truckData.gate,
      duration:
        normalizedTruckType === "TRUCK"
          ? calculateTruckDuration(truckData.jobStartDate, truckData.jobEndDate)
          : truckData.gatepassDuration.toUpperCase(),
      permanentMonths:
        truckData.gatepassDuration === "permanent"
          ? Number(truckData.permanentDuration ?? "1")
          : undefined,
      truckType: normalizedTruckType,
      cargoDestination,
      vehicles: mappedVehicles,
      deliveryNotes: (truckData.deliveryNotesDocuments ?? [])
        .map(mapTruckDeliveryNote)
        .filter((item) => Boolean(item.url)),
      cargos: mapTruckCargos(truckData),
      ...(Object.keys(jobDetails).length > 0 ? { jobDetails } : {}),
    };
  }

  return basePayload;
}

/** Normalize API taskDecision.attachments (string or rich object) for preview UI. */
export function normalizeGatepassTaskDecisionAttachment(
  att:
    | string
    | { url?: string; documentUrl?: string; originalFileName?: string | null; type?: string },
  fallbackType: string,
): { url: string; type: string; originalFileName?: string | null } {
  if (typeof att === "string") {
    return { url: att, type: fallbackType, originalFileName: undefined };
  }
  const url = att.url ?? att.documentUrl ?? "";
  return {
    url: String(url),
    type: typeof att.type === "string" && att.type.trim() ? att.type : fallbackType,
    originalFileName: att.originalFileName,
  };
}

// Format port options for VisitorDetails Form section
export function formatPortOptions(ports: PortCode[]) {
  return ports
    .map((port) => ({
      label: port.portName ?? "",
      value: port.portCode ?? "",
    }))
    .filter((opt) => opt.value && opt.label);
}

export function formatDateToDdMmYyyy(value?: string | Date | null): string {
  if (!value) return "";

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "";

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();

  return `${day}/${month}/${year}`;
}
